import React, { useState } from "react";

import {
    Box, 
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    styled,
    Card
} from '@mui/material';

import {
  LoadingButton
} from '@mui/lab';

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { useItems, itemTypes, targetTypes } from '../../../hooks/api/useItems';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { initialValues, validationSchema } from "../../../schema/products/categorySchema";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewCategory({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@product"] })

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useItems({ itemType: itemTypes.category, targetType: targetTypes.category }); 
    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.category.id)

    const onSubmit = async (currentValues) => {

        try{
            
            let data = {
                item_category: {
                    parent_category_id: "0",
                    name: currentValues?.categoryName,
                    description: currentValues?.description,
                    status: currentValues?.status? 1 : 0,
                }
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("products/categories"))
                
        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }   

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;  

    const RenderForm = () => {

        return(
            
            <form onSubmit={formik.handleSubmit}>
                
                <StyledCard>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                            
                            <CommonSectionHeader
                                title={t('information')}
                                subTitle={t('provideDetailsOfTheProfile')}
                            />                   
                            
                        </Grid>
                        <Grid item xs={12} sm={12} md={9}>
                            <Grid container spacing={1}>

                                <Grid item xs={12} sm={12} md={6} textAlign="left">
                                    <FormControlLabel 
                                        control={
                                            <Switch 
                                                name="status" 
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }                          
                                        label={       
                                            Boolean(values.status)? t('enabled') : t('disabled')
                                        }
                                    />
                                </Grid>

                                <Grid item xs={6}>    
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"categoryName"}
                                        label={t('categoryName')}
                                        type="text"                                         
                                        variant="outlined"
                                        value={values.categoryName}
                                        onChange={handleChange}
                                        error={touched.categoryName && Boolean(errors.categoryName)}
                                        helperText={touched.categoryName && errors.categoryName}
                                    />
                                </Grid>

                                <Grid item xs={6}>    
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t('description')}
                                        type="text"                                         
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2}}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{ 
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>    
        )
    }

    return (
        <Box>
            {  
                getHtmlHead({ routeType, title: headTitles.newCategory })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('newCategory')}
                breadcrumbs={[
                    {
                        displayText: t('categories'),
                        props: {
                            href: getRoute("products/categories")
                        }
                    },
                    {
                        displayText: t('newCategory'),
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            { 
                isPermissionAllow({ functionType: functionTypes.pageNew.id })?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}